// errorWheel.js — THE canonical iYuga "something went wrong" mark.
// Gordon design ask (2026-07-25, verbatim): "'Something went wrong' needs an
// image designed for it - red hued Wheel with an exclamation point in the
// middle?"  Spec: memory/error-state-wheel-mark-spec.md.
//
// COMPOSED, NEVER RE-DRAWN (G-19 / everything-is-a-shared-component):
// the silhouette comes straight from `brandWheelInner` — the same canonical
// renderer BrandWheelLoader uses (0.75 Portrait ratio, parallel-edged 4u gaps,
// six unequal spans, viewBox 120). Nothing here draws an arc. Every brand-mark
// bug caught on 2026-07-25 (the purple loader, the MUI clone glyph, the crammed
// login wordmark) came from a surface re-deriving a brand asset instead of
// composing it, so this module has exactly one job: recolour + fill the centre.
//
// ONE RED, NO NEW REDS (UI-062 killed a three-reds drift): every fill in this
// file derives from the single token `field.errorText` (#D92D20, tokens.js —
// "THE one field-error red"). The wheel reads red-hued but calm because the
// segments are ALPHA TINTS of that one hex, not six different reds.
//
// CONTRAST: the lowest alpha in the ramp is 0.72, which composites on a white
// surface to rgb(228,104,94) => 3.27:1 against #FFFFFF — above the WCAG 2.2
// 1.4.11 non-text minimum of 3:1. The exclamation is the undiluted #D92D20
// (4.83:1). Do not lower MIN_ALPHA without re-checking those numbers.
//
// This module is framework-free (pure string output) so the geometry can be
// asserted in a plain unit test and reused at build time. React wrapper:
// ./index.jsx.

import { brandWheelInner, BRAND_WHEEL_SPANS } from "../BrandWheel/brandWheel";
import { field } from "../../theme/tokens";

// The single source hex. Imported, never re-typed.
export const ERROR_RED = field.errorText; // #D92D20

// Segment tint ramp. Red-DOMINANT (the first segment is the undiluted token) and
// tonal rather than six hues, so the mark reads "error" without shouting. Six
// values for the six canonical unequal spans. All >= MIN_ALPHA.
export const MIN_ALPHA = 0.72;
export const ERROR_WHEEL_ALPHAS = [1, 0.78, 0.92, 0.72, 0.86, 0.96];

/** "#D92D20" -> [217, 45, 32] */
export function hexToRgb(hex) {
  const h = String(hex).replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** The six segment fills, all derived from ERROR_RED at the ramp's alphas. */
export function errorWheelColors(hex = ERROR_RED, alphas = ERROR_WHEEL_ALPHAS) {
  const [r, g, b] = hexToRgb(hex);
  return alphas.map((a) =>
    a >= 1 ? hex : `rgba(${r}, ${g}, ${b}, ${a})`
  );
}

// The exclamation, drawn in the PORTRAIT position (the white centre disc) — which
// is also how this mark satisfies the standing "never a bare/hollow wheel centre"
// law. Geometry is in the shared viewBox-120 space: centre (60,60), Portrait
// radius 42, white disc radius 40.5. The glyph's furthest point from centre is
// the bar's top corner at 26.6u, so it sits comfortably inside the disc at every
// size. A stadium bar + a separated dot — the universal exclamation, not a font
// glyph (no webfont dependency, no metric drift between platforms).
export const EXCLAMATION_BAR = { x: 54.5, y: 34, width: 11, height: 34, rx: 5.5 };
export const EXCLAMATION_DOT = { cx: 60, cy: 79, r: 6 };

export function exclamationSVG(fill = ERROR_RED) {
  const b = EXCLAMATION_BAR;
  const d = EXCLAMATION_DOT;
  return (
    `<rect class="ewl-bang ewl-bang-bar" x="${b.x}" y="${b.y}" width="${b.width}" ` +
    `height="${b.height}" rx="${b.rx}" fill="${fill}"/>` +
    `<circle class="ewl-bang ewl-bang-dot" cx="${d.cx}" cy="${d.cy}" r="${d.r}" fill="${fill}"/>`
  );
}

/**
 * Inner SVG markup for the error mark: the canonical brand Wheel in the error
 * tint ramp, white Portrait disc, exclamation in the centre.
 * Returned WITHOUT the <svg> wrapper (same contract as brandWheelInner).
 * @returns {string}
 */
export function errorWheelInner(opts = {}) {
  const { hex = ERROR_RED, spans = BRAND_WHEEL_SPANS, alphas = ERROR_WHEEL_ALPHAS } = opts;
  return brandWheelInner({
    spans,
    colors: errorWheelColors(hex, alphas),
    disc: true,
    portrait: exclamationSVG(hex),
  });
}

/** Full standalone SVG string (build-time / non-React use). */
export function errorWheelSVG(opts = {}) {
  const { size = 96, ariaLabel = "" } = opts;
  const aria = ariaLabel ? `role="img" aria-label="${ariaLabel}"` : `aria-hidden="true"`;
  return (
    `<svg ${aria} width="${size}" height="${size}" viewBox="0 0 120 120" ` +
    `style="display:block;flex:0 0 auto">${errorWheelInner(opts)}</svg>`
  );
}

export default errorWheelSVG;
